import React, { useCallback, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import toast, { Toast } from 'react-hot-toast';
import { Button, Loader } from 'ncoded-component-library';
import BannerWrapper from 'components/BannerWrapper';
import DataRoomFileAccess from 'components/DataRoomFileAccess';
import { DataRoomFileAccessRef } from 'components/DataRoomFileAccess/DataRoomFileAccess.component';
import CheckIcon from 'icons/Check.icon';
import LockclosedIcon from 'icons/Lockclosed.icon';
import useBusEvent from 'hooks/useBusEvent';
import useCallbackAsync from 'hooks/useCallbackAsync';
import useCallbackRef from 'hooks/useCallbackRef';
import useToast from 'hooks/useToast';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { CallbackBusType, StandardEventTypes } from 'modules/bus';
import { ChatSessionFile } from 'models/Message';
import {
  ChatAttachmentToastEventProps,
  DocumentPublishedEventProps,
} from 'models/MixpanelEvents';
import { COLOR_VARIANT } from 'models/Colors';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { getIsSellSide } from 'router/subrouters/DataRoom/utils';
import { EIGHT_SECONDS } from 'data/global';
import { getArrayLength, getIsArrayEmpty, getIsOwner } from 'utils';
import MessagingContext from '../../providers/Messaging/Messaging.context';
import ChatFilesContext from '../../providers/ChatFiles/ChatFiles.context';

import './SaveChatFilesToDataRoomToast.styles.scss';

/**
 * The two toasts of the flow, in order. Holding them in one state is what keeps them
 * from ever showing at the same time — they share a single toast, so the second
 * replaces the first — and makes a new send replace a toast still on screen, so there
 * is one toast per message rather than one per attachment.
 */
type ToastState =
  | { step: 'save'; files: ChatSessionFile[] }
  | { step: 'saved'; nodes: DataRoomNode[] }
  | null;

type SaveChatFilesToDataRoomToastProps = {
  className?: string;
};

/**
 * Offers the sender of a message with attachments to save them to the Data Room, and
 * once saved, to manage who can see them — the Data Room drawer opens over the chat,
 * so the conversation is never left behind.
 */
const SaveChatFilesToDataRoomToast: React.FC<
  SaveChatFilesToDataRoomToastProps
> = (props) => {
  const { className } = props;

  const [state, setState] = useState<ToastState>(null);

  const [accessDrawer, accessDrawerRef] =
    useCallbackRef<DataRoomFileAccessRef>();

  const classes = classNames('save-chat-files-to-data-room-toast', className);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const { userSide, currentSession } = useContext(MessagingContext);
  const { onFilesAddToDataRoom } = useContext(ChatFilesContext);
  const {
    currentUser: { _id: currentUserId },
  } = useContext(CurrentUserContext);

  const {
    dataRoomId,
    name: dataRoomName,
    userId: ownerId,
  } = currentSession?.deal || {};

  const isSellSide = getIsSellSide(userSide);

  const getToastEventProps = useCallback(
    (attachmentCount: number) =>
      getMixpanelProps({
        attachmentCount,
        userSide,
      }) as ChatAttachmentToastEventProps,
    [getMixpanelProps, userSide],
  );

  const filesSent = useCallback(
    ({ payload }: CallbackBusType<ChatSessionFile[]>) => {
      // files the sender can't publish (no Data Room on the deal) or that are in the
      // Data Room already leave nothing to offer
      const files = payload.filter(
        ({ canMoveFileToDataRoom, isDataRoomFile }) =>
          canMoveFileToDataRoom && !isDataRoomFile,
      );

      if (getIsArrayEmpty(files)) return;

      setState({ step: 'save', files });

      // the event belongs to the toast, so it fires once for the message, not once
      // per attachment
      mixpanelService.chatAttachmentToastShown(
        getToastEventProps(getArrayLength(files)),
      );
    },
    [getToastEventProps],
  );

  // only the sender broadcasts this, so recipients never see a toast
  useBusEvent(StandardEventTypes.FILES_SENT, filesSent);

  const [saveToDataRoom, isSaving] = useCallbackAsync(async () => {
    if (state?.step !== 'save') return;

    const { files } = state;
    const isOwner = getIsOwner(ownerId, currentUserId);

    mixpanelService.chatAttachmentToastSaveClicked(
      getToastEventProps(getArrayLength(files)),
    );

    // one event per file, matching the "Files in chat" menu action
    files.forEach(({ file: { name, type, originalType } }) =>
      mixpanelService.documentPublished(
        getMixpanelProps({
          dataRoomId,
          dataRoomName,
          documentName: name,
          documentType: originalType || type,
          isOwner,
          userSide,
        }) as DocumentPublishedEventProps,
      ),
    );

    const nodes = await onFilesAddToDataRoom(files.map(({ _id }) => _id));

    setState({ step: 'saved', nodes });
  });

  const manageAccess = useCallback(() => {
    if (state?.step !== 'saved') return;

    mixpanelService.chatAttachmentManageAccessClicked(
      getToastEventProps(getArrayLength(state.nodes)),
    );

    accessDrawer?.open(state.nodes);
    setState(null);
  }, [accessDrawer, state, getToastEventProps]);

  // each toast carries an action, so it is given longer than the default before it
  // goes away on its own. The timer restarts for the second toast, and holds while
  // the save is in flight.
  useEffect(() => {
    if (!state || isSaving) return;

    const timeout = setTimeout(() => setState(null), EIGHT_SECONDS);

    return () => clearTimeout(timeout);
  }, [state, isSaving]);

  const render = useCallback(
    ({ id, visible }: Toast) => {
      const isSaved = state?.step === 'saved';
      const count = getArrayLength(isSaved ? state.nodes : state?.files);

      return (
        <BannerWrapper
          className={classes}
          isVisible={visible}
          color={COLOR_VARIANT.LIGHT_GREY}
          onClose={() => {
            toast.dismiss(id);
            setState(null);
          }}
        >
          <div className="save-chat-files-to-data-room-toast__content">
            {isSaved ? <LockclosedIcon /> : <CheckIcon />}

            <div className="save-chat-files-to-data-room-toast__text">
              <p>
                {t(isSaved ? 'fileSavedToDataRoom' : 'fileSentToChat', {
                  count,
                })}
              </p>
              <p>
                {isSaved
                  ? t(
                      isSellSide
                        ? 'onlyDealOwnerAndAdminsCanSeeIt'
                        : 'onlyAdminsInYourBuyerGroupCanSeeIt',
                      { count },
                    )
                  : t('saveToDataRoomToKeepWithDealDocuments', { count })}
              </p>
            </div>
          </div>

          <div className="save-chat-files-to-data-room-toast__actions">
            <Button
              variant="outline"
              disabled={isSaving}
              onClick={isSaved ? manageAccess : saveToDataRoom}
            >
              {isSaving && <Loader inline overlay={false} size="small" />}
              {isSaved
                ? t('manageAccess')
                : t('saveFilesToDataRoom', { count })}
            </Button>
          </div>
        </BannerWrapper>
      );
    },
    [classes, isSaving, isSellSide, state, manageAccess, saveToDataRoom, t],
  );

  useToast({ visible: !!state, render });

  return (
    <DataRoomFileAccess
      ref={accessDrawerRef}
      dataRoomId={dataRoomId}
      prefetch={state?.step === 'saved'}
      scrollToBuySide
    />
  );
};

export default SaveChatFilesToDataRoomToast;
